// src/components/AgentProgress.tsx

import React, { useState, useEffect } from "react";
import "./AgentProgress.css";

interface AgentProgressProps {
  analysisModel: string;
  isActive: boolean;
}

interface AgentStep {
  name: string;
  description: string;
  status: "pending" | "running" | "completed" | "error";
}

const AgentProgress: React.FC<AgentProgressProps> = ({ analysisModel, isActive }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<AgentStep[]>([]);

  useEffect(() => {
    if (!isActive) {
      // When analysis completes, mark all steps as completed briefly before hiding
      setSteps((prevSteps) =>
        prevSteps.map((step) => ({ ...step, status: "completed" as const }))
      );
      setTimeout(() => {
        setCurrentStep(0);
        setSteps([]);
      }, 1000);
      return;
    }

    // Define agent steps based on analysis model
    let agentSteps: AgentStep[] = [];

    if (analysisModel === "classic") {
      agentSteps = [
        { name: "Technical Analyst", description: "Analyzing technical indicators and chart patterns", status: "pending" },
        { name: "Relative Strength Scout", description: "Evaluating relative strength vs market and sector", status: "pending" },
        { name: "Macro Context Agent", description: "Assessing macroeconomic conditions", status: "pending" },
        { name: "Risk Manager", description: "Evaluating risk factors and position sizing", status: "pending" },
        { name: "Strategy Synthesizer", description: "Synthesizing trade recommendation", status: "pending" },
        { name: "Verification Agent", description: "Validating recommendation quality", status: "pending" },
      ];
    } else if (analysisModel === "multi_turn_debate") {
      agentSteps = [
        { name: "Technical Analyst", description: "Analyzing technical indicators", status: "pending" },
        { name: "Relative Strength Scout", description: "Evaluating relative strength", status: "pending" },
        { name: "Macro Context Agent", description: "Assessing macro conditions", status: "pending" },
        { name: "Proposal Agents", description: "Generating strategy proposals", status: "pending" },
        { name: "Critic Agent", description: "Evaluating proposals", status: "pending" },
        { name: "Judge Agent", description: "Selecting winning proposal", status: "pending" },
      ];
    } else if (analysisModel === "chart_pattern_analysis") {
      agentSteps = [
        { name: "Chart Pattern Analyzers", description: "Running 10 chart pattern strategies", status: "pending" },
        { name: "Pattern Consolidator", description: "Consolidating pattern results", status: "pending" },
        { name: "Pattern Verifier", description: "Verifying pattern quality", status: "pending" },
      ];
    } else if (analysisModel === "soxl_tactical_10m") {
      agentSteps = [
        { name: "Data Collection", description: "Fetching price data and indicators", status: "pending" },
        { name: "Regime Classification", description: "Classifying market regime", status: "pending" },
        { name: "Setup Detection", description: "Detecting trade setups", status: "pending" },
        { name: "Strategy Evaluation", description: "Evaluating tactical strategy", status: "pending" },
      ];
    } else {
      agentSteps = [
        { name: "Analysis", description: "Running analysis", status: "pending" },
      ];
    }

    setSteps(agentSteps);
    setCurrentStep(0);

    // Simulate progress through steps
    // Progress faster through early steps, slower through later steps
    const stepDelays = agentSteps.map((_, idx) => {
      if (idx === 0) return 2000; // First step: 2 seconds
      if (idx < agentSteps.length - 1) return 2500; // Middle steps: 2.5 seconds
      return 4000; // Last step: 4 seconds (longer as it's the final processing)
    });

    let stepIndex = 0;
    const intervals: ReturnType<typeof setTimeout>[] = [];

    const scheduleNextStep = () => {
      if (stepIndex < agentSteps.length) {
        const delay = stepIndex < stepDelays.length ? stepDelays[stepIndex] : 3000;
        const timeout = setTimeout(() => {
          setCurrentStep(stepIndex);
          stepIndex++;
          scheduleNextStep();
        }, delay);
        intervals.push(timeout);
      }
    };

    scheduleNextStep();

    return () => {
      intervals.forEach(clearTimeout);
    };
  }, [analysisModel, isActive]);

  useEffect(() => {
    if (!isActive || steps.length === 0) return;

    // Update step statuses based on currentStep
    setSteps((prevSteps) =>
      prevSteps.map((step, index) => {
        if (index < currentStep) {
          return { ...step, status: "completed" as const };
        } else if (index === currentStep) {
          return { ...step, status: "running" as const };
        } else {
          return { ...step, status: "pending" as const };
        }
      })
    );
  }, [currentStep, isActive, steps.length]);

  // Don't render if not active and no steps
  if (!isActive && steps.length === 0) {
    return null;
  }

  // If active but no steps yet, show a loading state
  if (isActive && steps.length === 0) {
    return (
      <div className="agent-progress-container">
        <div className="agent-progress-header">
          <h3>Agent Progress</h3>
          <div className="agent-progress-subtitle">Initializing analysis...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-progress-container">
      <div className="agent-progress-header">
        <h3>Agent Progress</h3>
        <div className="agent-progress-subtitle">Analysis in progress...</div>
      </div>
      <div className="agent-progress-steps">
        {steps.map((step, index) => (
          <div
            key={index}
            className={`agent-progress-step agent-progress-step-${step.status}`}
          >
            <div className="agent-progress-step-indicator">
              {step.status === "completed" && (
                <svg className="agent-progress-checkmark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              )}
              {step.status === "running" && (
                <div className="agent-progress-spinner">
                  <div className="agent-progress-spinner-circle"></div>
                </div>
              )}
              {step.status === "pending" && (
                <div className="agent-progress-pending"></div>
              )}
            </div>
            <div className="agent-progress-step-content">
              <div className="agent-progress-step-name">{step.name}</div>
              <div className="agent-progress-step-description">{step.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AgentProgress;

